import apiClient from './apiService';

// --- Types ---

export interface Posting {
  id: number;
  position_id: number;
  user_id: number;
  entity_id: number;
  reference_text?: string;
  posting_type?: string;   // Direct | Promotion | Rotation | Vacancy
  action?: string;         // JOIN | LEAVE
  batch_ref?: string;      // Links related legs of same HR action
  start_date: string;
  end_date?: string | null;
  is_active: number;
  is_approved: number;
  created_by?: number;
  created_on?: string;
  modified_by?: number;
  modified_on?: string;
  // Joined fields
  position_name?: string;
  position_code?: string;
  authority_name?: string;
  location_name?: string;
  user_name?: string;
  code_seq?: number | null;
  code?: string | null;
}

export interface QueryParams {
  search?: string;
  type?: string;      // Direct | Promotion | Rotation | Vacancy
  action?: string;    // JOIN | LEAVE
  is_active?: number; // 1 = current, 0 = ended
  page?: number;
  limit?: number;
}

export interface PaginatedResponse {
  data: Posting[];
  meta: { page: number; limit: number; total: number; pages: number };
}

export interface VacantPosition {
  id: number;
  name: string;
  code: string;
  authority_name?: string;
  location_name?: string;
  is_approved?: number;
}

export interface OccupiedPosition {
  id: number;
  name: string;
  code: string;
  authority_name?: string;
  location_name?: string;
  posting_id: number;
  user_id: number;
  occupant_name: string;
}

export interface DirectData {
  position_id: number;
  user_id: number;
  start_date: string;
  reference_text?: string;
}

export interface VacancyData {
  position_id: number;
  user_id: number;
  start_date: string;
  reference_text?: string;
}

export interface RotationData {
  position_a_id: number;
  position_b_id: number;
  effective_date: string;
  reference_text?: string;
}

export interface PromotionData {
  user_id: number;
  from_position_id: number;
  to_position_id: number;
  effective_date: string;
  reference_text?: string;
  relieve_target_incumbent: boolean;
}

// --- Service ---

class PostingService {
  private basePath = '/api/v1/org/postings';

  async getAll(params?: QueryParams): Promise<PaginatedResponse> {
    const qp = new URLSearchParams();
    if (params?.type) qp.append('type', params.type);
    if (params?.action) qp.append('action', params.action);
    if (params?.search) qp.append('search', params.search);
    if (params?.is_active !== undefined) qp.append('is_active', params.is_active.toString());
    if (params?.page) qp.append('page', params.page.toString());
    if (params?.limit) qp.append('limit', params.limit.toString());

    const qs = qp.toString();
    const url = `${this.basePath}${qs ? `?${qs}` : ''}`;
    const response = await apiClient.get<any>(url);
    const d = response.data?.data || response.data;
    const meta = response.data?.meta || { page: 1, limit: 20, total: 0, pages: 0 };
    return { data: Array.isArray(d) ? d : [], meta };
  }

  async getByPosition(positionId: number): Promise<Posting[]> {
    const response = await apiClient.get<any>(`${this.basePath}/by-position/${positionId}`);
    return response.data?.data || response.data || [];
  }

  async getByUser(userId: number): Promise<Posting[]> {
    const response = await apiClient.get<any>(`${this.basePath}/by-user/${userId}`);
    return response.data?.data || response.data || [];
  }

  // --- Workflow actions ---

  async direct(data: DirectData): Promise<any> {
    const response = await apiClient.post<any>(`${this.basePath}/direct`, data);
    return response.data;
  }

  async vacancy(data: VacancyData): Promise<any> {
    const response = await apiClient.post<any>(`${this.basePath}/vacancy`, data);
    return response.data;
  }

  async rotate(data: RotationData): Promise<any> {
    const response = await apiClient.post<any>(`${this.basePath}/rotate`, data);
    return response.data;
  }

  async promote(data: PromotionData): Promise<any> {
    const response = await apiClient.post<any>(`${this.basePath}/promote`, data);
    return response.data;
  }

  // --- Position helpers ---

  async getVacantPositions(): Promise<VacantPosition[]> {
    const response = await apiClient.get<any>('/api/v1/org/positions/vacant');
    return response.data?.data || response.data || [];
  }

  async getOccupiedPositions(): Promise<OccupiedPosition[]> {
    const response = await apiClient.get<any>('/api/v1/org/positions/occupied');
    return response.data?.data || response.data || [];
  }

  async submit(id: number): Promise<void> {
    await apiClient.post(`${this.basePath}/${id}/submit`, {});
  }

  async approve(id: number, note?: string): Promise<void> {
    await apiClient.post(`${this.basePath}/${id}/approve`, { note });
  }

  async reject(id: number, reason: string): Promise<void> {
    await apiClient.post(`${this.basePath}/${id}/reject`, { reason });
  }

  async reopen(id: number): Promise<void> {
    await apiClient.post(`${this.basePath}/${id}/reopen`, {});
  }

  async getHistory(id: number): Promise<any[]> {
    const response = await apiClient.get<{ items: any[] }>(`${this.basePath}/${id}/history`);
    const rows = response.data.items ?? (response.data as any) ?? [];
    return rows.map((r: any) => ({
      audit_id: r.id,
      entity_id: r.entity_id,
      action: r.event_type === 'state_transition' ? 'STATE' : 'EDIT',
      changed_by: r.changed_by,
      changed_by_username: r.changed_by_username || 'Unknown',
      changed_on: r.changed_on,
      change_reason: r.remarks,
    }));
  }
}

const postingService = new PostingService();
export default postingService;
